import React from "react"
import { useDispatch, useSelector } from "react-redux"

import { Box, colors } from "@mui/material"

import { ColumnTypeEnum } from "@/codegen/graphql-request"
import { masterRecordsSlice, useErrors } from "@/slice/masterRecordsSlice"
import { useColumnTypes, useReferenceConfigs } from "@/slice/masterSchemaSlice"
import { RootState } from "@/store"
import { ColumnData } from "@/type"
import { convertToStore, encodeDatetime } from "@/util/converter"
import { gqlClient } from "@/util/graphql"

import { DateTimePicker } from "./DateTimePicker"
import { ErrorIcon } from "./ErrorIcon"
import { ReferenceItemPicker } from "./ReferenceItemPicker"
import { TextInput } from "./TextInput"
import { TextInputForReference } from "./TextInputForReference"
import { TimePeriodPicker } from "./TimePeriodPicker"

export const Cell = React.memo(
	(props: { recordId: string; columnName: string; tableName: string; data: ColumnData; displayData: string }) => {
		const { recordId, columnName, tableName, data, displayData } = props
		const dispatch = useDispatch()

		// store

		const continueEditMode = useSelector((state: RootState) => state.ui.continueEditMode)
		const columnTypes = useColumnTypes()[tableName]
		const columnType = columnTypes[columnName]
		const referenceConfig = useReferenceConfigs(tableName)[columnName]
		const errors = useErrors(recordId, columnName)

		// local state

		const [isEdit, setIsEdit] = React.useState(false)
		const [inputValue, setInputValue] = React.useState("")

		// functions

		const updateCellWithConvert = React.useCallback(
			async (value: string) => {
				const convertedValue = convertToStore(value, columnType)
				await gqlClient()
					.updateMasterRecord({ recordId, columnName, columnData: { value: convertedValue } })
					.then(({ updateMasterRecord: { errors } }) => {
						if (errors) {
							dispatch(masterRecordsSlice.actions.setErrors([{ recordId, columnName, errors }]))
						} else {
							dispatch(
								masterRecordsSlice.actions.setColumnData({
									tableName,
									recordId,
									columnName,
									data: convertedValue,
									columnType,
								}),
							)
							dispatch(masterRecordsSlice.actions.setErrors([{ recordId, columnName, errors: undefined }]))
						}
					})
			},
			[columnType, columnName, dispatch, recordId, tableName],
		)

		const leaveEditMode = React.useCallback(async () => {
			if (continueEditMode == false) {
				if (inputValue != data) {
					await updateCellWithConvert(inputValue)
				}
				setIsEdit(false)
			}
		}, [continueEditMode, data, inputValue, updateCellWithConvert])

		const enterEditMode = React.useCallback(() => {
			setIsEdit(true)
		}, [])

		React.useEffect(() => {
			if (isEdit == true && continueEditMode == false) {
				leaveEditMode()
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [continueEditMode])

		React.useEffect(() => {
			if (columnType == "datetime") {
				setInputValue(encodeDatetime(data as string, "YYYY-MM-DDTHH:mm"))
			} else {
				setInputValue(displayData)
			}
		}, [columnType, data, displayData])

		return (
			<Box height="32px" position="relative">
				{columnType == "datetime" ? (
					<DateTimePicker
						inputValue={inputValue}
						displayData={displayData}
						leaveEditMode={leaveEditMode}
						enterEditMode={enterEditMode}
						setInputValue={setInputValue}
						isEdit={isEdit}
					/>
				) : columnType == "time_period" ? (
					<Box sx={{ height: "100%", px: "8px", color: colors.grey[800] }}>
						<TimePeriodPicker initialValue={inputValue} onPick={updateCellWithConvert} />
					</Box>
				) : referenceConfig ? (
					<TextInputForReference
						inputValue={inputValue}
						leaveEditMode={leaveEditMode}
						enterEditMode={enterEditMode}
						setInputValue={setInputValue}
						reference={referenceConfig}
						isEdit={isEdit}
						columnType={columnType}
						cellData={data}
					/>
				) : (
					<TextInput
						inputValue={inputValue}
						leaveEditMode={leaveEditMode}
						enterEditMode={enterEditMode}
						setInputValue={setInputValue}
						isEdit={isEdit}
					/>
				)}
				{errors && (
					<ErrorIcon
						errors={errors}
						rightSpace={columnType == ColumnTypeEnum.Datetime || columnType == ColumnTypeEnum.TimePeriod}
					/>
				)}
				{isEdit && referenceConfig && (
					<Box
						bgcolor="white"
						border={1}
						borderColor="grey.300"
						position="absolute"
						overflow="auto"
						minWidth="100%"
						boxSizing="content-box"
						left="-1px"
						zIndex="tooltip"
					>
						<ReferenceItemPicker reference={referenceConfig} columnType={columnType} setInputValue={setInputValue} />
					</Box>
				)}
			</Box>
		)
	},
)
